/**
 * ColaTrabajoTable — Table displaying the work queue for procesamiento.
 *
 * Columns: patient name, order number, exam name, result type, priority (colored badge),
 * barcode, wait time, status.
 * STAT rows in red background, URGENTE in orange.
 * Click on row opens the capture Sheet.
 *
 * Requirements: 6.2, 6.3, 6.4, 15.3, 15.4
 */

import { useTranslation } from "react-i18next";
import { Clock, FlaskConical } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { ColaTrabajoItem } from "@/hooks/use-procesamiento";
import { getPrioridadRowClass, getPrioridadBadgeVariant } from "../utils/priority-utils";

interface ColaTrabajoTableProps {
  data: ColaTrabajoItem[];
  isLoading: boolean;
  onRowClick: (item: ColaTrabajoItem) => void;
}

export function ColaTrabajoTable({ data, isLoading, onRowClick }: ColaTrabajoTableProps) {
  const { t } = useTranslation();

  const formatWaitTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
        <FlaskConical className="h-10 w-10 mb-2 opacity-30" />
        <p>{t("procesamiento.cola.sin_examenes")}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("procesamiento.cola.columnas.paciente")}</TableHead>
            <TableHead>{t("procesamiento.cola.columnas.orden")}</TableHead>
            <TableHead>{t("procesamiento.cola.columnas.examen")}</TableHead>
            <TableHead className="hidden md:table-cell">
              {t("procesamiento.cola.columnas.tipo_resultado")}
            </TableHead>
            <TableHead>{t("procesamiento.cola.columnas.prioridad")}</TableHead>
            <TableHead className="hidden lg:table-cell">
              {t("procesamiento.cola.columnas.codigo_barras")}
            </TableHead>
            <TableHead>{t("procesamiento.cola.columnas.tiempo_espera")}</TableHead>
            <TableHead>{t("procesamiento.cola.columnas.estado")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item) => (
            <TableRow
              key={item.id}
              className={cn(
                "cursor-pointer transition-colors hover:bg-muted/50",
                getPrioridadRowClass(item.prioridad)
              )}
              onClick={() => onRowClick(item)}
            >
              {/* Paciente */}
              <TableCell className="font-medium max-w-[200px]">
                <div className="truncate">{item.paciente_nombre}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {item.paciente_documento}
                </div>
              </TableCell>

              {/* Orden */}
              <TableCell>
                <span className="font-mono text-xs">{item.numero_orden}</span>
              </TableCell>

              {/* Examen */}
              <TableCell>
                <div className="truncate max-w-[180px]">{item.examen_nombre}</div>
                <div className="text-xs text-muted-foreground">{item.examen_area_display}</div>
              </TableCell>

              {/* Tipo resultado */}
              <TableCell className="hidden md:table-cell">
                <Badge variant="outline" className="text-xs">
                  {item.tipo_resultado}
                </Badge>
              </TableCell>

              {/* Prioridad */}
              <TableCell>
                <Badge variant={getPrioridadBadgeVariant(item.prioridad)}>
                  {item.prioridad_display}
                </Badge>
              </TableCell>

              {/* Código de barras */}
              <TableCell className="hidden lg:table-cell">
                {item.codigo_barras_muestra ? (
                  <span className="font-mono text-xs">{item.codigo_barras_muestra}</span>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </TableCell>

              {/* Tiempo de espera */}
              <TableCell>
                <div className="flex items-center gap-1 text-xs">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span
                    className={cn(
                      item.tiempo_espera_minutos > 120
                        ? "text-destructive font-medium"
                        : item.tiempo_espera_minutos > 60
                          ? "text-orange-600 dark:text-orange-400"
                          : "text-muted-foreground"
                    )}
                  >
                    {formatWaitTime(item.tiempo_espera_minutos)}
                  </span>
                </div>
              </TableCell>

              {/* Estado */}
              <TableCell>
                <Badge
                  variant={item.estado === "EN_PROCESO" ? "default" : "secondary"}
                  className="text-xs"
                >
                  {item.estado_display}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
